/**
 * Regras das etiquetas — puras, testáveis sem Firebase.
 *
 * Ficam fora do componente porque o que se grava aqui é lido pelo inbox inteiro:
 * uma cor inválida não quebra nada em teste e vira um chip invisível na tela do
 * atendente, e um `espaco` errado esconde a etiqueta do time que ia usá-la.
 */

/**
 * Paleta sugerida — atalho, não camisa de força.
 *
 * A etiqueta é lida de relance numa lista com dezenas de conversas: o que
 * importa é distinguir uma da outra, não acertar o tom, e escolher de uma
 * paleta evita dois azuis quase iguais no primeiro dia. Mas a cor livre existe
 * ao lado (`<input type="color">`), para a clínica que quer a cor da marca.
 * São as cores do seed mais algumas, todas com contraste para texto branco.
 */
export const CORES = [
  { valor: '#dc2626', nome: 'Vermelho' },
  { valor: '#ea580c', nome: 'Laranja' },
  { valor: '#f59e0b', nome: 'Âmbar' },
  { valor: '#16a34a', nome: 'Verde' },
  { valor: '#0f766e', nome: 'Verde-azulado' },
  { valor: '#0ea5e9', nome: 'Azul' },
  { valor: '#4f46e5', nome: 'Índigo' },
  { valor: '#a855f7', nome: 'Roxo' },
  { valor: '#db2777', nome: 'Rosa' },
  { valor: '#64748b', nome: 'Cinza' },
];

export const COR_PADRAO = CORES[5].valor;

/**
 * Onde a etiqueta aparece — SEMPRE um espaço só.
 *
 * 'ambos' saiu (23/ago). Etiqueta e resposta rápida passaram a ser da EQUIPE DO
 * SETOR, e as `firestore.rules` autorizam cada papel apenas no seu espaço: um
 * documento 'ambos' não teria dono — nem a recepção nem o SDR poderia editá-lo,
 * só o gestor. Não havia nada cadastrado em produção, então não há migração.
 *
 * O `ConversasProvider` NÃO mudou: ele ainda aceita `!e.espaco` e 'ambos' na
 * leitura, o que mantém compatível qualquer documento antigo que apareça.
 */
export const ESPACOS = [
  { valor: 'vendas', rotulo: 'Vendas / SDR' },
  { valor: 'recepcao', rotulo: 'Recepção' },
];

const VALORES_ESPACO = new Set(ESPACOS.map((e) => e.valor));

export const LIMITE_NOME = 30;

/**
 * Nome normalizado: sem espaço sobrando e sem quebra de linha.
 *
 * O chip é de uma linha só; um `\n` colado de outro lugar esticaria a lista
 * inteira do inbox.
 */
export const normalizarNome = (bruto) =>
  String(bruto ?? '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, LIMITE_NOME);

/**
 * `null` quando a etiqueta serve; a mensagem de recusa quando não.
 *
 * @param existentes as etiquetas já cadastradas, para barrar nome repetido
 * @param idAtual    o id em edição, que não conta como conflito consigo mesmo
 */
export function recusar({ nome, cor, espaco }, existentes = [], idAtual = null) {
  const limpo = normalizarNome(nome);
  if (!limpo) return 'Dê um nome à etiqueta.';

  // Nome repetido não quebra o sistema — os ids são distintos —, mas deixa o
  // atendente escolhendo entre dois chips idênticos, sem saber qual é qual.
  const repetido = existentes.some(
    (e) => e.id !== idAtual && normalizarNome(e.nome).toLowerCase() === limpo.toLowerCase(),
  );
  if (repetido) return `Já existe uma etiqueta chamada "${limpo}".`;

  if (!ehHex(cor)) return 'Escolha uma cor válida.';
  if (!VALORES_ESPACO.has(espaco)) return 'Escolha onde a etiqueta aparece.';
  return null;
}

/**
 * Hex de 3 ou 6 dígitos. O `<input type="color">` sempre devolve 6, mas uma cor
 * digitada à mão ou vinda de documento antigo pode estar na forma curta.
 */
const ehHex = (valor) => /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(String(valor ?? ''));

/** Cor normalizada em minúsculas, ou a padrão se vier algo que não é cor. */
export const sanitizarCor = (bruta) => {
  const v = String(bruta ?? '').trim().toLowerCase();
  return ehHex(v) ? v : COR_PADRAO;
};

/**
 * Espaço válido, ou 'recepcao'.
 *
 * NÃO existe mais um padrão neutro: toda etiqueta pertence a um setor, e é o
 * setor que decide quem pode editá-la. Na prática a tela sempre passa o espaço
 * da rota, então o fallback só existe para não gravar `undefined`.
 */
export const sanitizarEspaco = (bruto) =>
  VALORES_ESPACO.has(String(bruto ?? '')) ? String(bruto) : 'recepcao';

/**
 * O documento como vai para o Firestore, já saneado.
 *
 * A sanitização mora aqui e não no componente: é este objeto que chega ao banco,
 * e um campo torto gravado só aparece depois, como chip invisível na tela do
 * atendente.
 */
export const paraDocumento = ({ nome, cor, espaco }) => ({
  nome: normalizarNome(nome),
  cor: sanitizarCor(cor),
  espaco: sanitizarEspaco(espaco),
});

/** Rótulo do espaço, para a listagem. */
export const rotuloDoEspaco = (valor) =>
  ESPACOS.find((e) => e.valor === valor)?.rotulo ?? 'Os dois espaços';

/** Só as do espaço pedido. Documento antigo sem `espaco` fica de fora. */
export const doEspaco = (itens, espaco) =>
  (itens ?? []).filter((i) => i.espaco === espaco);
